import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import toast from "react-hot-toast";
import {
  addArtworkImages,
  deleteArtwork,
  deleteArtworkImage,
  detectCrops,
  splitArtwork,
  thumbUrl,
  updateArtwork,
  updateArtworkImages,
  updateCrop,
} from "./artGalleryApi";
import { PRESENTATIONS, SITE_URL, STATUSES, TYPES } from "./artGalleryShared";
import { Field, RichText, Segmented, TextField, Toggle } from "./ArtGalleryFields";

const toPercentCrop = (box, artwork) => ({
  unit: "%",
  x: (box.x / artwork.originalWidth) * 100,
  y: (box.y / artwork.originalHeight) * 100,
  width: (box.width / artwork.originalWidth) * 100,
  height: (box.height / artwork.originalHeight) * 100,
});

const toPixelCrop = (crop, artwork) => ({
  x: Math.round((crop.x / 100) * artwork.originalWidth),
  y: Math.round((crop.y / 100) * artwork.originalHeight),
  width: Math.round((crop.width / 100) * artwork.originalWidth),
  height: Math.round((crop.height / 100) * artwork.originalHeight),
});

const sameCrop = (a, b) =>
  ["x", "y", "width", "height"].every((key) => Math.abs(a[key] - b[key]) <= 1);

const ASPECT_TOLERANCE = 0.03;

const roundCm = (value) => Math.round(value * 10) / 10;

const numberOrNull = (value) => (value === "" || value === null ? null : Number(value));

const idOf = (value) => (value && typeof value === "object" ? value._id : value) || "";

const escapeHtml = (value) => value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const textToHtml = (value = "") =>
  value
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br>")}</p>`)
    .join("");

const FRAMED_OPTIONS = [
  { id: true, label: "Framed" },
  { id: false, label: "Unframed" },
  { id: null, label: "Not set" },
];

const initialForm = (artwork) => ({
  title: artwork.title || "",
  medium: artwork.medium || "",
  price: artwork.price ?? "",
  salePrice: artwork.salePrice ?? "",
  priceOnRequest: !!artwork.priceOnRequest,
  status: artwork.status,
  widthCm: artwork.widthCm ?? "",
  heightCm: artwork.heightCm ?? "",
  depthCm: artwork.depthCm ?? "",
  artist: idOf(artwork.artist),
  type: artwork.type || "original",
  subjects: (artwork.subjects || []).map(idOf),
  editionSize: artwork.editionSize ?? "",
  framed: artwork.framed ?? null,
  frameNotes: artwork.frameNotes || "",
  published: !!artwork.published,
  slug: artwork.slug || "",
  presentation: artwork.presentation || "shadow",
  isNewWork: !!artwork.isNewWork,
  featured: !!artwork.featured,
  heroCandidate: !!artwork.heroCandidate,
  description: artwork.description || "",
  shortDescription: artwork.shortDescription || "",
  descriptionHtml: artwork.descriptionHtml || textToHtml(artwork.description || ""),
});

const ExtraImages = ({ artwork, onSaved, onError }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const images = artwork.images || [];

  const run = async (task) => {
    setBusy(true);
    try {
      onSaved([await task()]);
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const reorder = (from, to) => {
    const next = [...images];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    run(() => updateArtworkImages(artwork._id, next.map((img) => ({ _id: img._id, alt: img.alt }))));
  };

  return (
    <div className="ag-field">
      <span className="ag-field-label">More photos</span>
      <div className="ag-gallery-field">
        {images.map((image, index) => (
          <div className="ag-gallery-item" key={image._id}>
            <img src={thumbUrl(image.url, 240)} alt={image.alt || ""} />
            <div className="ag-gallery-item-actions">
              <button type="button" disabled={busy || index === 0} onClick={() => reorder(index, index - 1)} aria-label="Move left">
                ←
              </button>
              <button type="button" disabled={busy || index === images.length - 1} onClick={() => reorder(index, index + 1)} aria-label="Move right">
                →
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => window.confirm("Remove this photo?") && run(() => deleteArtworkImage(artwork._id, image._id))}
                aria-label="Remove"
              >
                ✕
              </button>
            </div>
          </div>
        ))}
        <button type="button" className="ag-gallery-add" onClick={() => inputRef.current?.click()} disabled={busy}>
          {busy ? "Working..." : "+ Add photos"}
        </button>
      </div>
      <span className="ag-field-hint">Detail shots, the back, or the piece in a room. Shown after the main image.</span>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        hidden
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length) run(() => addArtworkImages(artwork._id, files));
        }}
      />
    </div>
  );
};

const ArtworkEditor = ({ artwork, section = "website", artists = [], subjects = [], onClose, onSaved, onDeleted, onError }) => {
  const isWebsite = section === "website";
  const [crop, setCrop] = useState(() => toPercentCrop(artwork.crop, artwork));
  const [form, setForm] = useState(() => initialForm(artwork));
  const [lockAspect, setLockAspect] = useState(() => {
    if (!artwork.widthCm || !artwork.heightCm) return true;
    const savedAspect = artwork.widthCm / artwork.heightCm;
    const cropShape = artwork.crop.width / artwork.crop.height;
    return Math.abs(savedAspect / cropShape - 1) < ASPECT_TOLERANCE;
  });
  const [detected, setDetected] = useState(null);
  const [busy, setBusy] = useState("");

  const pixelCrop = useMemo(() => toPixelCrop(crop, artwork), [crop, artwork]);
  const aspect = pixelCrop.height ? pixelCrop.width / pixelCrop.height : 1;

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  // keep the height in step with the crop shape while the aspect lock is on
  const applyCrop = (next) => {
    setCrop(next);
    const px = toPixelCrop(next, artwork);
    if (!lockAspect || !px.height || form.widthCm === "") return;
    setForm((f) => ({ ...f, heightCm: roundCm(Number(f.widthCm) / (px.width / px.height)) }));
  };

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const setDimension = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => {
      const next = { ...f, [key]: value };
      if (lockAspect && value !== "" && Number(value) > 0) {
        if (key === "widthCm") next.heightCm = roundCm(Number(value) / aspect);
        else next.widthCm = roundCm(Number(value) * aspect);
      }
      return next;
    });
  };

  const toggleSubject = (id) =>
    setForm((f) => ({ ...f, subjects: f.subjects.includes(id) ? f.subjects.filter((s) => s !== id) : [...f.subjects, id] }));

  const rerunAutoCrop = async () => {
    setBusy("detect");
    try {
      const boxes = await detectCrops(artwork._id);
      applyCrop(toPercentCrop(boxes[0], artwork));
      setDetected(boxes);
      toast.success(
        boxes.length > 1 ? `Found ${boxes.length} pieces in this photo` : "Auto-crop applied - adjust if needed"
      );
    } catch (error) {
      onError(error);
    } finally {
      setBusy("");
    }
  };

  const split = async () => {
    setBusy("split");
    try {
      onSaved(await splitArtwork(artwork._id, detected));
      toast.success(`Split into ${detected.length} artworks`);
      onClose();
    } catch (error) {
      onError(error);
      setBusy("");
    }
  };

  const presentationFields = () => ({
    title: form.title,
    medium: form.medium,
    description: form.description,
    status: form.status,
    price: numberOrNull(form.price),
    widthCm: numberOrNull(form.widthCm),
    heightCm: numberOrNull(form.heightCm),
    needsReview: false,
  });

  const websiteFields = () => {
    const fields = {
      title: form.title,
      medium: form.medium,
      status: form.status,
      price: numberOrNull(form.price),
      salePrice: numberOrNull(form.salePrice),
      priceOnRequest: form.priceOnRequest,
      widthCm: numberOrNull(form.widthCm),
      heightCm: numberOrNull(form.heightCm),
      depthCm: numberOrNull(form.depthCm),
      editionSize: form.type === "limited-edition" ? numberOrNull(form.editionSize) : null,
      artist: form.artist || null,
      type: form.type,
      subjects: form.subjects,
      framed: form.framed,
      frameNotes: form.frameNotes,
      published: form.published,
      presentation: form.presentation,
      isNewWork: form.isNewWork,
      featured: form.featured,
      heroCandidate: form.heroCandidate,
      shortDescription: form.shortDescription,
      descriptionHtml: form.descriptionHtml,
      needsReview: false,
    };
    if (form.slug.trim() && form.slug.trim() !== artwork.slug) fields.slug = form.slug.trim();
    return fields;
  };

  const save = async () => {
    const fields = isWebsite ? websiteFields() : presentationFields();
    for (const key of ["price", "salePrice", "widthCm", "heightCm", "depthCm", "editionSize"].filter((k) => k in fields)) {
      if (fields[key] !== null && !(fields[key] >= 0)) {
        toast.error(`Enter a valid ${key.replace(/Cm$/, "").replace("salePrice", "sale price")}`);
        return;
      }
    }
    if (fields.published && !fields.title.trim()) {
      toast.error("Add a title before publishing");
      return;
    }
    if (fields.published && fields.price === null && !fields.priceOnRequest) {
      toast.error("Add a price, or tick 'Price on request', before publishing");
      return;
    }

    setBusy("save");
    try {
      let updated = await updateArtwork(artwork._id, fields);
      if (!sameCrop(pixelCrop, artwork.crop)) {
        updated = await updateCrop(artwork._id, pixelCrop);
      }
      onSaved([updated]);
      toast.success(updated.published ? "Saved - the website will update in a few seconds" : "Saved");
      onClose();
    } catch (error) {
      onError(error);
      setBusy("");
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${artwork.title || "this artwork"}"? This can't be undone.`)) return;
    setBusy("delete");
    try {
      await deleteArtwork(artwork._id);
      onDeleted(artwork._id);
    } catch (error) {
      onError(error);
      setBusy("");
    }
  };

  return (
    <div className="ag-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="ag-modal" role="dialog" aria-modal="true">
        <div className="ag-editor-crop">
          <ReactCrop crop={crop} onChange={(_, percent) => applyCrop(percent)} keepSelection minWidth={20} minHeight={20}>
            <img src={artwork.originalImageUrl} alt="Original upload" />
          </ReactCrop>
          <div className="ag-editor-crop-actions">
            <button className="ag-btn ag-btn-ghost" onClick={rerunAutoCrop} disabled={!!busy}>
              {busy === "detect" ? "Detecting..." : "Re-run auto-crop"}
            </button>
            <button
              className="ag-btn ag-btn-ghost"
              onClick={() => applyCrop({ unit: "%", x: 0, y: 0, width: 100, height: 100 })}
              disabled={!!busy}
            >
              Use whole photo
            </button>
            {detected && detected.length > 1 && (
              <button className="ag-btn ag-btn-primary" onClick={split} disabled={!!busy}>
                Split into {detected.length} artworks
              </button>
            )}
            <span className="ag-editor-hint">
              Drag the corners to the edge of the painted canvas. {pixelCrop.width} × {pixelCrop.height}px
            </span>
          </div>
        </div>

        <div className="ag-editor-form">
          <div className={`ag-editor-preview is-${form.presentation}`}>
            <div
              className="ag-editor-preview-image"
              style={{
                aspectRatio: `${aspect}`,
                backgroundImage: `url(${artwork.originalImageUrl})`,
                backgroundSize: `${10000 / crop.width}% ${10000 / crop.height}%`,
                backgroundPosition: `${crop.width >= 100 ? 0 : (crop.x / (100 - crop.width)) * 100}% ${
                  crop.height >= 100 ? 0 : (crop.y / (100 - crop.height)) * 100
                }%`,
              }}
            />
          </div>

          <section className="ag-editor-section">
            <TextField label="Title" value={form.title} onChange={set("title")} placeholder="Untitled" />
            {isWebsite && (
            <div className="ag-field-row">
              <Field label="Artist">
                <select value={form.artist} onChange={(e) => set("artist")(e.target.value)}>
                  <option value="">No artist</option>
                  {artists.map((a) => (
                    <option key={a._id} value={a._id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Type">
                <select value={form.type} onChange={(e) => set("type")(e.target.value)}>
                  {TYPES.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            )}
            <div className="ag-field-row">
              <TextField label="Medium" value={form.medium} onChange={set("medium")} placeholder="e.g. Oil on canvas" />
              {form.type === "limited-edition" && (
                <Field label="Edition of">
                  <input type="number" min="1" step="1" value={form.editionSize} onChange={(e) => set("editionSize")(e.target.value)} />
                </Field>
              )}
            </div>

            <div className="ag-field-row">
              <Field label="Width (cm)">
                <input type="number" min="0" step="0.1" value={form.widthCm} onChange={setDimension("widthCm")} />
              </Field>
              <Field label="Height (cm)">
                <input type="number" min="0" step="0.1" value={form.heightCm} onChange={setDimension("heightCm")} />
              </Field>
              {isWebsite && (
                <Field label="Depth (cm)">
                  <input type="number" min="0" step="0.1" value={form.depthCm} onChange={(e) => set("depthCm")(e.target.value)} />
                </Field>
              )}
            </div>
            <label className="ag-checkbox">
              <input type="checkbox" checked={lockAspect} onChange={(e) => setLockAspect(e.target.checked)} />
              Match the crop's proportions ({aspect.toFixed(2)}:1)
            </label>

            {isWebsite && (
              <Field label="Frame">
                <Segmented options={FRAMED_OPTIONS} value={form.framed} onChange={set("framed")} full />
              </Field>
            )}
            {isWebsite && form.framed && (
              <TextField label="Frame notes" value={form.frameNotes} onChange={set("frameNotes")} placeholder="e.g. Framed size 72 x 58 cm" />
            )}

            {isWebsite && subjects.length > 0 && (
              <Field label="Subjects">
                <div className="ag-chip-select">
                  {subjects.map((s) => (
                    <button
                      key={s._id}
                      type="button"
                      className={`ag-chip ${form.subjects.includes(s._id) ? "is-active" : ""}`}
                      onClick={() => toggleSubject(s._id)}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              </Field>
            )}
          </section>

          <section className="ag-editor-section">
            <h3>Price &amp; availability</h3>
            <div className="ag-field-row">
              <Field label="Price (£)">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.price}
                  onChange={(e) => set("price")(e.target.value)}
                  disabled={isWebsite && form.priceOnRequest}
                />
              </Field>
              {isWebsite && (
                <Field label="Sale price (£)" hint="Leave blank if not on sale">
                  <input type="number" min="0" step="1" value={form.salePrice} onChange={(e) => set("salePrice")(e.target.value)} disabled={form.priceOnRequest} />
                </Field>
              )}
            </div>
            {isWebsite && (
              <label className="ag-checkbox">
                <input type="checkbox" checked={form.priceOnRequest} onChange={(e) => set("priceOnRequest")(e.target.checked)} />
                Price on request (hide the price on the website)
              </label>
            )}
            <Field label="Status">
              <Segmented options={STATUSES} value={form.status} onChange={set("status")} full />
            </Field>
          </section>

          {isWebsite ? (
            <>
              <section className="ag-editor-section">
                <h3>Website</h3>
                <Toggle
                  label="Published"
                  hint={form.published ? "Visible on the website" : "Draft - only visible here"}
                  checked={form.published}
                  onChange={set("published")}
                />
                <TextField
                  label="Web address"
                  value={form.slug}
                  onChange={set("slug")}
                  placeholder="Created from the title when published"
                  hint={form.slug ? `${SITE_URL.replace(/^https?:\/\//, "")}/artworks/${form.slug}` : undefined}
                />
                <Field label="Presentation">
                  <Segmented options={PRESENTATIONS} value={form.presentation} onChange={set("presentation")} full />
                  <span className="ag-field-hint">{PRESENTATIONS.find((p) => p.id === form.presentation)?.hint}</span>
                </Field>
                <Toggle label="New work" hint="Shown in New works on the homepage" checked={form.isNewWork} onChange={set("isNewWork")} />
                <Toggle label="Featured" hint="Prioritised in the default collection order" checked={form.featured} onChange={set("featured")} />
                <Toggle label="Homepage hero" hint="Can be chosen as the large homepage image" checked={form.heroCandidate} onChange={set("heroCandidate")} />
                {artwork.published && artwork.slug && (
                  <a className="ag-link-btn" href={`${SITE_URL}/artworks/${artwork.slug}`} target="_blank" rel="noreferrer">
                    View on website ↗
                  </a>
                )}
              </section>

              <section className="ag-editor-section">
                <h3>Description</h3>
                <TextField
                  label="Summary"
                  multiline
                  rows={2}
                  value={form.shortDescription}
                  onChange={set("shortDescription")}
                  hint="One or two sentences, used in search results and link previews"
                />
                <RichText label="Full description" value={form.descriptionHtml} onChange={set("descriptionHtml")} />
              </section>

              <section className="ag-editor-section">
                <ExtraImages artwork={artwork} onSaved={onSaved} onError={onError} />
              </section>
            </>
          ) : (
            <section className="ag-editor-section">
              <TextField label="Description" multiline rows={4} value={form.description} onChange={set("description")} />
            </section>
          )}

          <div className="ag-editor-actions">
            <button className="ag-btn ag-btn-danger-ghost" onClick={remove} disabled={!!busy}>
              Delete
            </button>
            <span />
            <button className="ag-btn ag-btn-ghost" onClick={onClose} disabled={!!busy}>
              Cancel
            </button>
            <button className="ag-btn ag-btn-primary" onClick={save} disabled={!!busy}>
              {busy === "save" ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtworkEditor;
